<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: number;
  text: string;
  action?: ToastAction;
  variant?: 'default' | 'danger';
  durationMs?: number;
}

const toasts = ref<ToastMessage[]>([]);
let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function dismiss(id: number): void {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  toasts.value = toasts.value.filter((x) => x.id !== id);
}

function showToast(msg: Omit<ToastMessage, 'id'>): number {
  const id = nextId++;
  const entry: ToastMessage = { id, variant: 'default', durationMs: 5000, ...msg };
  toasts.value = [...toasts.value, entry];
  const ms = entry.durationMs ?? 5000;
  const timer = setTimeout(() => dismiss(id), ms);
  timers.set(id, timer);
  return id;
}

function clearAll(): void {
  for (const id of timers.keys()) dismiss(id);
}

onMounted(() => {
  (window as Window & { __appToast?: typeof toastApi }).__appToast = toastApi;
});

onUnmounted(() => {
  clearAll();
});

const toastApi = { show: showToast, dismiss };
</script>

<template>
  <div
    class="app-toast-host"
    aria-live="polite"
  >
    <div
      v-for="t in toasts"
      :key="t.id"
      :class="['app-toast', t.variant === 'danger' ? 'app-toast--danger' : '']"
    >
      <span class="app-toast__text">{{ t.text }}</span>
      <button
        v-if="t.action"
        type="button"
        class="app-toast__action"
        @click="t.action.onClick()"
      >
        {{ t.action.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.app-toast-host {
  position: fixed;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1300;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
  max-width: min(420px, calc(100vw - 2rem));
}

.app-toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  background: var(--text);
  color: var(--surface, #fff);
  font-family: var(--font-ui);
  font-size: 0.875rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.app-toast--danger {
  background: var(--danger, #c0492f);
}

.app-toast__action {
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}
</style>
